import React, { createContext, useContext, useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Theme } from "utils/constants/BrandConfig";

interface MetadataContextType {
  favicon: string;
  title: string;
  description: string;
  keywords: string[];
}

const MetadataContext = createContext<MetadataContextType | undefined>(
  undefined
);

interface MetadataProviderProps {
  theme: Theme;
  children: React.ReactNode;
}

const checkAssetExists = async (path: string): Promise<boolean> => {
  try {
    const response = await fetch(path, { method: "HEAD" });
    return response.ok;
  } catch {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = path;
    });
  }
};

export const MetadataProvider: React.FC<MetadataProviderProps> = ({
  theme,
  children,
}) => {
  const defaultFaviconPath = "/favicon.ico";
  const [metadata, setMetadata] = React.useState(theme.metadata);
  useEffect(() => {
    const updateMetadata = async () => {
      const selectedMetadata = theme.metadata || {};
      const faviconExists = await checkAssetExists(theme.metadata?.favicon);
      if (!faviconExists) {
        console.warn(`Favicon not found: ${theme.metadata?.favicon}`);
      }
      setMetadata({
        ...selectedMetadata,
        favicon: faviconExists ? theme.metadata.favicon : defaultFaviconPath,
      });
    };
    updateMetadata();
  }, [theme.metadata]);

  return (
    <HelmetProvider>
      <MetadataContext.Provider value={{ ...theme.metadata }}>
        <Helmet prioritizeSeoTags>
          {metadata.title?.length && <title>{metadata.title}</title>}
          {metadata.description?.length && (
            <meta name="description" content={metadata.description} />
          )}
          {metadata.keywords?.length && (
            <meta name="keywords" content={metadata.keywords.join(", ")} />
          )}
          {metadata.favicon?.length && (
            <link rel="icon" href={metadata.favicon} />
          )}
        </Helmet>
        {children}
      </MetadataContext.Provider>
    </HelmetProvider>
  );
};

export const useMetadataContext = (): MetadataContextType => {
  const context = useContext(MetadataContext);
  if (!context) {
    throw new Error(
      "useMetadataContext must be used within a MetadataProvider"
    );
  }
  return context;
};
